import { useState, useEffect } from 'react'
import { shuService } from '../../services/shuService'
import { settingsService } from '../../services/settingsService'
import { showSuccess, showError, showWarning, showInfo, showConfirm } from '../../utils/alert';


function SHUCalculation() {
  const [year, setYear] = useState(new Date().getFullYear())
  const [calculationResult, setCalculationResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [shuSettings, setShuSettings] = useState(null)
  const [shuPeriod, setShuPeriod] = useState({ startMonth: 1, endMonth: 12 })

  useEffect(() => {
    fetchSHUSettings()
    fetchSHUPeriod()
  }, [year])

  const fetchSHUSettings = async () => {
    try {
      const response = await shuService.getSHUSettings(year)
      if (response.data?.success) {
        setShuSettings(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching SHU settings:', error)
    }
  }

  const fetchSHUPeriod = async () => {
    try {
      const response = await settingsService.getSystemConfig()
      if (response.data?.success) {
        const config = response.data.data?.data || {}
        const start = parseInt(config.shu?.shu_bulan_mulai?.value || '1')
        const end = parseInt(config.shu?.shu_bulan_akhir?.value || '12')
        setShuPeriod({ startMonth: start, endMonth: end })
      }
    } catch (error) {
      console.error('Error fetching SHU period:', error)
    }
  }

  const calculateSHU = async () => {
    setLoading(true)
    try {
      const response = await shuService.calculateSHUWeighted(year)
      if (response.data?.success) {
        setCalculationResult(response.data.data)
        // toast.success('Perhitungan SHU berhasil')
      } else {
        showError('Error: ' + (response.data?.message || 'Gagal menghitung SHU'))
      }
    } catch (error) {
      showError('Error: ' + (error.response?.data?.message || 'Gagal menghitung SHU'))
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
  }

  const getMonthName = (monthNum) => {
    const months = [
      'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
      'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
    ]
    return months[monthNum - 1] || '-'
  }

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Perhitungan SHU</h1>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          {/* SHU Period Info */}
          <div className="row">
            <div className="col-md-12">
              <div className="card card-outline card-info">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-info-circle mr-2"></i>
                    Informasi Periode SHU
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <strong>Periode:</strong> {calculationResult?.period 
                        ? `${getMonthName(calculationResult.period.startMonth)} ${new Date(calculationResult.period.startDate).getFullYear()} - ${getMonthName(calculationResult.period.endMonth)} ${new Date(calculationResult.period.endDate).getFullYear()}`
                        : `${getMonthName(shuPeriod.startMonth)} ${year-1} - ${getMonthName(shuPeriod.endMonth)} ${year}`}
                    </div>
                    <div className="col-md-4">
                      <strong>Tahun:</strong> {year}
                    </div>
                    <div className="col-md-4">
                      <strong>Metode:</strong> Lama Mengendap (Weighted Deposits)
                    </div>
                  </div>
                  <div className="alert alert-info mt-3 mb-0">
                    <small>
                      <strong>Penjelasan Perhitungan:</strong><br/>
                      SHU dihitung berdasarkan <strong>lama mengendapnya saham</strong> setiap anggota dalam periode tahunan.
                      Contoh: Jika sukarela Rp 1.000.000 ditarik tanggal 1 April dan dimasukkan lagi tanggal 27 April,
                      maka lama mengendap hanya 3 hari (27-30 April), bukan 1 bulan penuh.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Calculation Controls */}
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Pengaturan Perhitungan</h3>
                </div>
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-3">
                      <label>Tahun Perhitungan</label>
                      <input
                        type="number"
                        className="form-control"
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())}
                        min="2000"
                        max="2100"
                      />
                    </div>
                    <div className="col-md-3">
                      <label>&nbsp;</label>
                      <button
                        className="btn btn-primary btn-block"
                        onClick={calculateSHU}
                        disabled={loading}
                      >
                        {loading ? (
                          <><i className="fas fa-spinner fa-spin mr-2"></i>Menghitung...</>
                        ) : (
                          <><i className="fas fa-calculator mr-2"></i>Hitung SHU</>
                        )}
                      </button>
                    </div>
                    <div className="col-md-6">
                      {shuSettings && (
                        <div className="alert alert-warning mb-0">
                          <small>
                            <strong>Total SHU Tersedia:</strong> {formatCurrency(shuSettings.DanaCadangan)}<br/>
                            <strong>Status:</strong> {shuSettings.IsFinal ? 'Final' : 'Belum Final'}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Calculation Results */}
          {calculationResult && (
            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header bg-success">
                    <h3 className="card-title text-white">
                      <i className="fas fa-chart-pie mr-2"></i>
                      Hasil Perhitungan SHU Tahun {calculationResult.year}
                    </h3>
                  </div>
                  <div className="card-body">
                    {/* Summary */}
                    <div className="row mb-4">
                      <div className="col-md-3">
                        <div className="small-box bg-info">
                          <div className="inner">
                            <h4>{formatCurrency(calculationResult.totalSHU)}</h4>
                            <p>Total SHU</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="small-box bg-success">
                          <div className="inner">
                            <h4>{formatCurrency(calculationResult.alokasi?.totalUntukAnggota)}</h4>
                            <p>SHU Untuk Anggota</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="small-box bg-warning">
                          <div className="inner">
                            <h4>{calculationResult.memberCount}</h4>
                            <p>Jumlah Anggota</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="small-box bg-primary">
                          <div className="inner">
                            <h4>{getMonthName(calculationResult.period.startMonth)} - {getMonthName(calculationResult.period.endMonth)}</h4>
                            <p>Periode</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Alokasi SHU Breakdown */}
                    {calculationResult.alokasi && (
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <div className="table-responsive">
                            <table className="table table-bordered">
                              <thead className="thead-light">
                                <tr>
                                  <th>Alokasi</th>
                                  <th className="text-right">Persentase</th>
                                  <th className="text-right">Jumlah</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>Jasa Modal (untuk anggota)</td>
                                  <td className="text-right">{calculationResult.alokasi.jasaModal.persen}%</td>
                                  <td className="text-right">{formatCurrency(calculationResult.alokasi.jasaModal.jumlah)}</td>
                                </tr>
                                <tr>
                                  <td>Jasa Anggota (untuk anggota)</td>
                                  <td className="text-right">{calculationResult.alokasi.jasaAnggota.persen}%</td>
                                  <td className="text-right">{formatCurrency(calculationResult.alokasi.jasaAnggota.jumlah)}</td>
                                </tr>
                                <tr className="font-weight-bold">
                                  <td>Total SHU Untuk Anggota</td>
                                  <td className="text-right">{parseFloat(calculationResult.alokasi.jasaModal.persen) + parseFloat(calculationResult.alokasi.jasaAnggota.persen)}%</td>
                                  <td className="text-right">{formatCurrency(calculationResult.alokasi.totalUntukAnggota)}</td>
                                </tr>
                                <tr className="table-secondary">
                                  <td>Dana Pengurus</td>
                                  <td className="text-right">{calculationResult.alokasi.danaPengurus.persen}%</td>
                                  <td className="text-right">{formatCurrency(calculationResult.alokasi.danaPengurus.jumlah)}</td>
                                </tr>
                                <tr className="table-secondary">
                                  <td>Dana Sosial</td>
                                  <td className="text-right">{calculationResult.alokasi.danaSosial.persen}%</td>
                                  <td className="text-right">{formatCurrency(calculationResult.alokasi.danaSosial.jumlah)}</td>
                                </tr>
                                <tr className="table-secondary">
                                  <td>Dana Cadangan</td>
                                  <td className="text-right">{calculationResult.alokasi.danaCadangan.persen}%</td>
                                  <td className="text-right">{formatCurrency(calculationResult.alokasi.danaCadangan.jumlah)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Member Details Table */}
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead className="thead-dark">
                          <tr>
                            <th>No</th>
                            <th>NBA</th>
                            <th>Nama</th>
                            <th className="text-right">Simpanan Pokok</th>
                            <th className="text-right">Simpanan Wajib</th>
                            <th className="text-right">Simpanan Sukarela</th>
                            <th className="text-right">Total Simpanan</th>
                            <th className="text-right">Weighted Deposits</th>
                            <th className="text-right">Bunga Reguler</th>
                            <th className="text-right">Bunga Insidentil</th>
                            <th className="text-right">Skor Partisipasi</th>
                            <th className="text-right">Proporsi (%)</th>
                            <th className="text-right">SHU Diterima</th>
                          </tr>
                        </thead>
                        <tbody>
                          {calculationResult.members.map((member, index) => (
                            <tr key={member.nba}>
                              <td>{index + 1}</td>
                              <td>{member.nba}</td>
                              <td>{member.nama}</td>
                              <td className="text-right">{formatCurrency(member.pokok)}</td>
                              <td className="text-right">{formatCurrency(member.wajib)}</td>
                              <td className="text-right">{formatCurrency(member.sukarela)}</td>
                              <td className="text-right font-weight-bold">{formatCurrency(member.totalSavings)}</td>
                              <td className="text-right">{formatCurrency(member.weightedDeposits)}</td>
                              <td className="text-right">{formatCurrency(member.regularInterestPaid)}</td>
                              <td className="text-right">{formatCurrency(member.incidentalInterestPaid)}</td>
                              <td className="text-right font-weight-bold">{formatCurrency(member.participationScore)}</td>
                              <td className="text-right">{member.proportion}%</td>
                              <td className="text-right font-weight-bold text-success">{formatCurrency(member.shuPortion)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="thead-dark">
                          <tr>
                            <th colSpan="7" className="text-right">TOTAL</th>
                            <th className="text-right">
                              {formatCurrency(calculationResult.members.reduce((sum, m) => sum + m.weightedDeposits, 0))}
                            </th>
                            <th className="text-right">
                              {formatCurrency(calculationResult.members.reduce((sum, m) => sum + m.regularInterestPaid, 0))}
                            </th>
                            <th className="text-right">
                              {formatCurrency(calculationResult.members.reduce((sum, m) => sum + m.incidentalInterestPaid, 0))}
                            </th>
                            <th className="text-right">
                              {formatCurrency(calculationResult.totalParticipationScore)}
                            </th>
                            <th className="text-right">100%</th>
                            <th className="text-right">
                              {formatCurrency(calculationResult.members.reduce((sum, m) => sum + m.shuPortion, 0))}
                            </th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Explanation */}
                    <div className="alert alert-info mt-3">
                      <h5><i className="fas fa-info-circle mr-2"></i>Cara Perhitungan:</h5>
                      <ul className="mb-0">
                        <li><strong>Weighted Deposits</strong> = Jumlah (Saldo × Jumlah Hari Mengendap) untuk setiap periode</li>
                        <li><strong>Bunga Pinjaman</strong> = Total bunga yang dibayar (pinjaman reguler + insidentil) dalam periode</li>
                        <li><strong>Skor Partisipasi</strong> = Weighted Deposits + (Bunga Pinjaman × 10)</li>
                        <li><strong>Proporsi</strong> = (Skor Partisipasi Anggota ÷ Total Skor Partisipasi) × 100%</li>
                        <li><strong>SHU Diterima</strong> = Proporsi × Total SHU Untuk Anggota (Jasa Modal + Jasa Anggota)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default SHUCalculation
